// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import ProfileSkeleton from '../components/ProfileSkeleton/ProfileSkeleton';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { InstantSearch, Configure } from 'react-instantsearch';
import ProfileForm from '../components/ProfileForm/ProfileForm';
import ProfileHeader from '../components/ProfileHeader/ProfileHeader';
import PostList from '../components/PostList';
import SearchBar from '../components/SearchBar';
import FriendSearch from '../components/FriendSearch/FriendSearch';
import FriendList from '../components/FriendList/FriendList';
import Page from '../components/Page';
import { PostProvider } from '../contexts/PostContext';
import { usePost } from '../hooks/usePost';
import useUserProfile from '../hooks/useUserProfile';
import useUserPosts from '../hooks/useUserPosts';
import useUser from '../hooks/useUser';
import useFriends from '../hooks/useFriends';
import { getSecuredSearchClient } from '../utils/secureSearch';
import { getProfileSubmitErrorKey } from '../utils/profile';
import { auth } from '../firebase';
import '../styles/Profile.css';

const Profile = ({ initialTab = 'friends' }) => {
  const { t } = useTranslation();
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const {
    user: currentUser,
    updateCompleteUserProfile,
    loading: userLoading,
    saving: profileSaving,
    saveError: profileSaveError,
  } = useUser();
  
  const profileUserId = paramUserId || (currentUser && currentUser.uid);

  // Use the new hooks
  const { 
    profile, 
    isCurrentUser, 
    loadingProfile, 
    profileError, 
    setProfile, 
    friendStatus, 
    sendFriendRequest,
    cancelFriendRequest,
    acceptRequest,
    declineRequest,
    removeFriendship
  } = useUserProfile(profileUserId);
  
  // Legacy posts feature: retained for backward compatibility with existing production data; remove once Records fully replace posts.
  const {
    userPosts,
    savedPosts,
    savedPostIds,
    initialPostsLoading,
    loadingMorePosts,
    postsError,
    setUserPosts,
    fetchMorePosts,
    hasMorePosts,
    fetchMoreSavedPosts,
    hasMoreSavedPosts
  } = useUserPosts(profileUserId);

  const {
    friends,
    loading: friendsLoading,
    error: friendsError
  } = useFriends();

  const [searchClient, setSearchClient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchState, setSearchState] = useState({});

  const { setPosts } = usePost();

  // Initialize secured search client
  useEffect(() => {
    let mounted = true;

    async function initializeSecuredSearch() {
      if (!currentUser) return;

      try {
        const client = await getSecuredSearchClient();
        if (mounted) {
          setSearchClient(client);
        }
      } catch (error) {
        console.error('Failed to initialize secured search:', error);
      }
    }

    initializeSecuredSearch();
    return () => { mounted = false; };
  }, [currentUser]);  

  // Reset activeTab when navigating to a different profile
  useEffect(() => {
    const nextTab =
      !isCurrentUser && (initialTab === 'saved' || initialTab === 'friends')
        ? 'posts'
        : initialTab;
    setActiveTab(nextTab);
  }, [profileUserId, initialTab, isCurrentUser]);

  const handleSearchStateChange = (nextSearchState) => {
    setSearchState(nextSearchState);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleEditProfile = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
  };

  const handleProfileUpdate = async (profileData, originalUsername, profilePicture) => {
    try {
      const completeUpdatedProfile = await updateCompleteUserProfile(
        profileData, 
        originalUsername, 
        profilePicture
      );
      setProfile(completeUpdatedProfile);
      setEditing(false);
    } catch (error) {
      console.error("Error updating profile in Profile page:", error);
      throw error;
    }
  };

  const handlePostDelete = (deletedPostId) => {
    setUserPosts((currentPosts) => currentPosts.filter((post) => post.id !== deletedPostId));
    setPosts((currentPosts) => currentPosts.filter((post) => post.id !== deletedPostId));
  };

  const handleFriendClick = (friendId) => {
    navigate(`/profile/${friendId}`);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };  

  const friendsTabActive = isCurrentUser && activeTab === 'friends';

  if (loadingProfile || userLoading || (!searchClient && !friendsTabActive)) {
    return <ProfileSkeleton />;
  }

  if (profileError || postsError) {
    return (
      <div className="profile-error">
        {t('error')}: {profileError || postsError}
      </div>
    );
  }

  if (!profile) {
    return <div className="profile-error">{t('profile.not_found')}</div>;
  }

  const isSavedView = activeTab === 'saved';

  const filterString = isSavedView
    ? (savedPostIds?.length
        ? `objectID:${savedPostIds.join(' OR objectID:')}`
        : 'objectID:__none__')
    : `userId:${profileUserId}`;

      return (
        <PostProvider>
          <Page width="narrow" className="profile-container">
            {editing ? (
              <ProfileForm
                mode="edit"
                initialValues={profile}
                onSubmit={handleProfileUpdate}
                onCancel={handleCancelEdit}
                pending={profileSaving}
                submitErrorKey={getProfileSubmitErrorKey(profileSaveError)}
              />
            ) : (
              <ProfileHeader
                user={{ ...profile, id: profileUserId }}
                isOwnProfile={isCurrentUser}
                friendshipStatus={friendStatus}
                onEditProfile={handleEditProfile}
                onLogout={handleLogout}
                onSendRequest={sendFriendRequest}
                onCancelRequest={cancelFriendRequest}
                onAcceptRequest={acceptRequest}
                onDeclineRequest={declineRequest}
                onUnfriend={removeFriendship}
              />
            )}
    
            <div className="profile-tabs">
              {isCurrentUser ? (
                <>
                  <button
                    className={`profile-tab ${friendsTabActive ? 'active' : ''}`}
                    onClick={() => handleTabChange('friends')}
                  >
                    {t('friends.my_friends')}
                  </button>
                  {/* Legacy posts UI: retained for backward compatibility with existing production data; remove once Records fully replace posts. */}
                  <button
                    className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
                    onClick={() => handleTabChange('posts')}
                  >
                    {t('posts.title')}
                  </button>
                  <button
                    className={`profile-tab ${activeTab === 'saved' ? 'active' : ''}`}
                    onClick={() => handleTabChange('saved')}
                  >
                    {t('profile.saved_posts')}
                  </button>
                </>
              ) : (
                <>
                  {/* Legacy posts UI: retained for backward compatibility with existing production data; remove once Records fully replace posts. */}
                  <button
                    className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
                    onClick={() => handleTabChange('posts')}
                  >
                    {t('posts.title')}
                  </button>
                </>
              )}
            </div>

            {friendsTabActive ? (
              <div className="profile-content">
                <FriendSearch />
                {friendsError ? (
                  <div className="profile-error">{friendsError}</div>
                ) : (
                  <FriendList
                    friends={friends}
                    isLoading={friendsLoading}
                    onFriendClick={handleFriendClick}
                  />
                )}
              </div>
            ) : (
              <>
                {/* Legacy posts UI: retained for backward compatibility with existing production data; remove once Records fully replace posts. */}
                <InstantSearch
                  searchClient={searchClient}
                  indexName={process.env.REACT_APP_ALGOLIA_INDEX_NAME}
                  searchState={searchState}
                  onSearchStateChange={handleSearchStateChange}
                >
                  <Configure filters={filterString} />
                  <div className="profile-posts-search">
                    <SearchBar />
                  </div>
                  <div className="profile-content">
                    <PostList
                      defaultPosts={isSavedView ? savedPosts : userPosts}
                      isCurrentUser={isCurrentUser}
                      activeTab={isSavedView ? 'saved' : 'posts'}
                      onPostDeleted={handlePostDelete}
                      fetchMorePosts={fetchMorePosts}
                      hasMorePosts={hasMorePosts}
                      fetchMoreSavedPosts={fetchMoreSavedPosts}
                      hasMoreSavedPosts={hasMoreSavedPosts}                  
                      initialLoading={initialPostsLoading}
                      loadingMore={loadingMorePosts}
                    />
                  </div>
                </InstantSearch>
              </>
            )}
          </Page>
        </PostProvider>
      );
    };
    
    export default Profile;
